import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { api } from 'src/common/utils/api';
import { ApiError } from 'src/error/ApiError';
import { notifyAndLogError, notifyAndWarn } from 'src/UI/common/ui-helpers';
import type { TemplateWithoutSections } from './useTemplates';

type DefaultCandidate = Pick<TemplateWithoutSections, 'id' | 'displayName' | 'isDefault'>;

/**
 * Makes a meeting note template the firm's default. Calling it on the template that is already
 * the default records the current user as having chosen it.
 */
export function useMakeDefaultTemplate() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<{ data: TemplateWithoutSections }, Error, string>({
    mutationFn: templateId => api.put<TemplateWithoutSections>(`templates/${templateId}/default`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['data-export-templates'] }),
  });

  const makeDefault = async (template: DefaultCandidate): Promise<boolean> => {
    try {
      await mutateAsync(template.id);
    } catch (error) {
      // A 400 means the list on screen is out of date (say, the template was deactivated since), so
      // trying again would fail the same way. core-api's message says why and is safe to show.
      if (error instanceof ApiError && error.status === 400) {
        queryClient.invalidateQueries({ queryKey: ['data-export-templates'] });
        notifyAndWarn("We couldn't change the default", error.message, { templateId: template.id });
        return false;
      }
      notifyAndLogError("We couldn't change the default", 'Please try again.', error, {
        templateId: template.id,
      });
      return false;
    }
    notifications.show({ ...successMessage(template), color: 'green' });
    return true;
  };

  return { makeDefault, isMakingDefault: isPending };
}

function successMessage(template: DefaultCandidate) {
  // Choosing the current default only records that the firm has confirmed it; nothing changes.
  if (template.isDefault) {
    return {
      title: 'Default confirmed',
      message: `We'll keep using ${template.displayName} to write notes automatically after every recorded meeting.`,
    };
  }
  return {
    title: `${template.displayName} is now your default`,
    message: "We'll use it to write notes automatically from your next recorded meeting.",
  };
}
